import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useShallowSelector } from '../Workspace2Store'

// Browser detection utilities
const isFirefox = () => navigator.userAgent.toLowerCase().includes('firefox')

interface CKEditorToggleResult<T> {
    /** A flag indicating whether a CKEditor instance is currently active. */
    active: boolean
    /** The key of the currently active CKEditor instance, or null if none is active. */
    activeEditorKey: T | null
    /** A reference to the CKEditor instance. */
    placeholderRef: RefObject<HTMLDivElement>
    /** A function to activate a CKEditor instance by key. */
    activate: (key?: T) => void
    /** A function to deactivate the currently active CKEditor instance. */
    deactivate: () => void
}

/**
 * A hook to manage CKEditor activation and deactivation.
 *
 * Features:
 * - Activates CKEditor when clicking on a placeholder.
 * - Deactivates CKEditor when clicking outside or losing focus (e.g., pressing Tab).
 * - Ensures only one editor is active at a time.
 * - Automatically deactivates all CKEditor instances when contentItemViewNodes changes in the workspace.
 *
 * @template T - The type of the key used to identify active CKEditor instances.
 * @param {T | null} contentItemId - The key/id of the content item associated with the CKEditor.
 * @returns {CKEditorToggleResult<T>} An object containing the state and functions for managing CKEditor activation and deactivation.
 */
export const useCKEditorToggle = <T>(
    contentItemId: T | null
): CKEditorToggleResult<T> => {
    const [activeEditorKey, setActiveEditorKey] = useState<T | null>(null)
    const placeholderRef = useRef<HTMLDivElement>(null)

    // subscribe to the global editor deactivation signal
    const { editorDeactivationSignal } = useShallowSelector((s) => ({
        editorDeactivationSignal: s.editorDeactivationSignal,
    }))

    const active = activeEditorKey === contentItemId

    // handler that Activates CKEditor using its key/id
    const activate = useCallback(
        (currentContentId?: T) =>
            setActiveEditorKey(currentContentId ?? contentItemId),
        [active, contentItemId]
    )

    // handler that Deactivates CKEditor when clicking outside or losing focus
    const deactivate = useCallback(() => setActiveEditorKey(null), [])

    // deactivate all instance of CKEditor when global deactivation signal changes
    useEffect(() => {
        if (editorDeactivationSignal > 0) {
            deactivate()
        }
    }, [editorDeactivationSignal, deactivate])

    /** Handle Firefox-specific text selection behavior */
    const handleFirefoxClick = useCallback(
        (target: HTMLElement) => {
            // delay the check to allow selection to complete
            setTimeout(() => {
                // only deactivate if there's no text selection
                const selection = window.getSelection()
                if (!selection || selection.toString().length === 0) {
                    if (
                        placeholderRef.current &&
                        !placeholderRef.current.contains(target)
                    ) {
                        deactivate()
                    }
                }
            }, 50)
        },
        [deactivate]
    )

    /** registers event listeners to handle outside clicks and loss of focus */
    useEffect(() => {
        const controller = new AbortController()
        const signal = controller.signal
        const browserIsFirefox = isFirefox()

        const handleBlurOrClickOutside = (event: Event) => {
            const target = event.target as HTMLElement
            // skip deactivation if the event is from CKEditor
            if (
                target?.closest('.ck-editor') ||
                target?.closest('.ck-balloon-panel')
            ) {
                return
            }

            if (browserIsFirefox && event.type === 'click') {
                handleFirefoxClick(target)
                return
            }

            if (
                placeholderRef.current &&
                !placeholderRef.current.contains(event.target as Node)
            ) {
                deactivate()
            }
        }

        if (activeEditorKey) {
            const eventType = browserIsFirefox ? 'click' : 'mousedown'
            document.addEventListener(eventType, handleBlurOrClickOutside, {
                signal,
            })
            document.addEventListener('focusin', handleBlurOrClickOutside, {
                signal,
            })
        } else {
            controller.abort()
        }

        return () => controller.abort()
    }, [activeEditorKey, deactivate, handleFirefoxClick])

    return {
        active,
        activeEditorKey,
        placeholderRef,
        activate,
        deactivate,
    }
}
